/* eslint-disable unicorn/filename-case */
// TODO(eslint): fix naming
import React, { useContext } from "react";
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Grid,
} from "@mui/material";
import { COVERAGE_FORM } from "@platform/rfp-core";
import { TextField, Typography } from "@platform/design-system";

import {
  BenchmarkSection,
  CommissionSection,
  SectionTitle,
} from "../../components";
import { RFP_ACTION_TYPES } from "../../constants";
import { handleNumericFieldChange } from "../utils";
import { RfpContext } from "../../contexts/rfpContext";
import CoverageButtonWrapper from "../components/coverageButtonWrapper";
import envVariables from "../../../../projectEnvVariables";
import CommentsField from "../components/commentsField";

import { createValidationHandlers } from "./coveragesValidations";

const {
  ALTERNATE_PLANS,
  ADDITIONAL_NOTES,
  CONTRIBUTION_TYPE,
  CONTRIBUTION_VALUE,
  CONTRIBUTION_STRUCTURE,
  CURRENT_COMMISSION,
  ENROLLED_LIVES,
  REQUESTED_COMMISSION,
  TOTAL_EMPLOYER_PAID,
  TOTAL_EMPLOYEE_PAID,
  CONTRIBUTORY,
  EMPLOYER_CONTRIBUTION_PERCENTAGE,
  EMPLOYER_CONTRIBUTION_AMOUNT,
  PLAN_TYPE,
  SINGLE_PLAN,
  PLANS,
  NO_PLAN,
} = COVERAGE_FORM;

const { UPDATE_SELECTED_COVERAGE } = RFP_ACTION_TYPES;

export const initialBasicLifeCoveragesDetails = {
  [PLAN_TYPE]: SINGLE_PLAN,
  [PLANS]: [
    {
      [CONTRIBUTION_STRUCTURE]: TOTAL_EMPLOYER_PAID,
      [CONTRIBUTION_TYPE]: "",
      [CONTRIBUTION_VALUE]: "",
      [ENROLLED_LIVES]: "",
      [CURRENT_COMMISSION]: "",
      [REQUESTED_COMMISSION]: "",
      [ADDITIONAL_NOTES]: "",
      [ALTERNATE_PLANS]: "",
      name: SINGLE_PLAN,
    },
  ],
};

const BasicLife = ({
  coverage,
  handleNext,
  isLastCoverage,
  nextCoverage,
  handleBack,
  prevCoverage,
  isFirstCoverage,
}) => {
  const { setRfp } = useContext(RfpContext);

  const handleFormChange = (updatedCoverage, currentPlanKey, fieldKey) => {
    setRfp({
      type: UPDATE_SELECTED_COVERAGE,
      value: updatedCoverage,
      currentPlanKey,
      fieldKey,
    });
  };

  return (
    <>
      <BasicLifeForm coverage={coverage} onFormChange={handleFormChange} />
      <CoverageButtonWrapper
        displayBackButton={!isFirstCoverage}
        displayNextButton={!isLastCoverage}
        backButtonName={prevCoverage?.name}
        nextButtonName={nextCoverage?.name}
        handleBackAction={handleBack}
        handleNextAction={handleNext}
      />
    </>
  );
};

export const BasicLifeForm = ({ coverage, onFormChange }) => {
  const isCurrentCoverage = coverage.isCurrentCoverage;
  const currentPlan = coverage.details[PLANS][0];
  const currentPlanErrors = currentPlan.errors || {};

  const handlePlanFormChange = ({ name, value }) => {
    const updatedPlan = { ...currentPlan };

    // reset contribution value field if contribution type is changed
    if (name === CONTRIBUTION_TYPE) {
      updatedPlan[CONTRIBUTION_VALUE] = "";
    }

    updatedPlan[name] = value;
    updatedPlan.displayName = NO_PLAN;

    const payload = {
      ...coverage,
      details: {
        ...coverage.details,
        [PLANS]: [updatedPlan],
      },
    };

    onFormChange(payload, 0, name);
  };

  const { handlePlanFormChangeWithValidation, handlePreviousFieldsValidation } =
    createValidationHandlers({
      currentPlan,
      coverage,
      onFormChange,
      isCurrentCoverage,
      activeTab: 0,
      planName: null,
    });

  return (
    <>
      {isCurrentCoverage && (
        <>
          <SectionTitle>Enrolled Lives</SectionTitle>
          <Grid container spacing={2} px={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                autoComplete="off"
                fullWidth
                name={ENROLLED_LIVES}
                error={!!currentPlanErrors[ENROLLED_LIVES]}
                value={currentPlan[ENROLLED_LIVES]}
                onChange={(e) => {
                  handleNumericFieldChange(
                    e.target,
                    currentPlan.name,
                    handlePlanFormChange,
                  );
                }}
                onFocus={() => {
                  handlePreviousFieldsValidation(ENROLLED_LIVES);
                }}
                onBlur={(e) => {
                  handlePlanFormChangeWithValidation(e.target);
                }}
                label={
                  <Typography>
                    Number of Enrolled Members{" "}
                    <span className={"required"}>*</span>
                  </Typography>
                }
              />
              {!!currentPlanErrors && (
                <Typography variant="caption" color="danger">
                  {currentPlanErrors[ENROLLED_LIVES]}
                </Typography>
              )}
            </Grid>
          </Grid>
        </>
      )}

      <SectionTitle>Contribution Structure</SectionTitle>
      <Box pl={2} pr={2}>
        <FormControl
          onFocus={() => {
            handlePreviousFieldsValidation(CONTRIBUTION_STRUCTURE);
          }}
        >
          <FormLabel id="contribution-structure-label" required>
            Select a Contribution Structure
          </FormLabel>
          <RadioGroup
            value={currentPlan[CONTRIBUTION_STRUCTURE]}
            row
            aria-labelledby="contribution-structure-label"
            name={CONTRIBUTION_STRUCTURE}
            onChange={(e) => {
              handlePlanFormChangeWithValidation(e.target);
            }}
          >
            <FormControlLabel
              value={TOTAL_EMPLOYER_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYER_PAID}
            />
            <FormControlLabel
              value={TOTAL_EMPLOYEE_PAID}
              control={<Radio />}
              label={TOTAL_EMPLOYEE_PAID}
            />
            <FormControlLabel
              value={CONTRIBUTORY}
              control={<Radio />}
              label={CONTRIBUTORY}
            />
          </RadioGroup>
        </FormControl>
        {currentPlan[CONTRIBUTION_STRUCTURE] === CONTRIBUTORY && (
          <Box mt={2}>
            <FormControl
              onFocus={() => {
                handlePreviousFieldsValidation(CONTRIBUTION_TYPE);
              }}
            >
              <FormLabel id="contribution-type-label">
                Add Contribution amount
              </FormLabel>
              <Box mt={1}>
                <RadioGroup
                  value={currentPlan[CONTRIBUTION_TYPE]}
                  aria-labelledby="contribution-type-label"
                  name={CONTRIBUTION_TYPE}
                  onChange={(e) => {
                    handlePlanFormChangeWithValidation(e.target);
                  }}
                >
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_PERCENTAGE}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_PERCENTAGE}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_PERCENTAGE && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE]}
                            onChange={(e) => {
                              handlePlanFormChange(e.target);
                            }}
                            onFocus={() => {
                              handlePreviousFieldsValidation(
                                CONTRIBUTION_VALUE,
                              );
                            }}
                            onBlur={(e) => {
                              handlePlanFormChangeWithValidation(e.target);
                            }}
                            label={<Typography>% percentage</Typography>}
                          />
                        )}
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={EMPLOYER_CONTRIBUTION_AMOUNT}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" fullWidth>
                        <Typography noWrap mr={1}>
                          {EMPLOYER_CONTRIBUTION_AMOUNT}
                        </Typography>
                        {currentPlan[CONTRIBUTION_TYPE] ===
                          EMPLOYER_CONTRIBUTION_AMOUNT && (
                          <TextField
                            autoComplete="off"
                            size="sm"
                            name={CONTRIBUTION_VALUE}
                            value={currentPlan[CONTRIBUTION_VALUE]}
                            onChange={(e) => {
                              handlePlanFormChange(e.target);
                            }}
                            onFocus={() => {
                              handlePreviousFieldsValidation(
                                CONTRIBUTION_VALUE,
                              );
                            }}
                            onBlur={(e) => {
                              handlePlanFormChangeWithValidation(e.target);
                            }}
                            label={<Typography>$ amount</Typography>}
                          />
                        )}
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>
            </FormControl>
          </Box>
        )}
        <Box>
          {!!currentPlanErrors && (
            <Typography variant="caption" color="danger">
              {currentPlanErrors[CONTRIBUTION_STRUCTURE]}
            </Typography>
          )}
        </Box>
      </Box>

      <CommissionSection
        handlePlanFormChange={handlePlanFormChange}
        handlePlanFormChangeWithValidation={handlePlanFormChangeWithValidation}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
        currentPlanErrors={currentPlanErrors}
        currentPlan={currentPlan}
        isCurrentCoverage={isCurrentCoverage}
      />

      {!isCurrentCoverage && (
        <BenchmarkSection
          handlePlanFormChange={handlePlanFormChange}
          currentPlan={currentPlan}
          link={envVariables.VITE_REACT_APP_BASIC_LIFE_BENCHMARKS_URL || "/"}
          coverageName={coverage?.name}
        />
      )}

      <CommentsField
        currentPlan={currentPlan}
        onChange={(e) => {
          handlePlanFormChange(e.target);
        }}
        handlePreviousFieldsValidation={handlePreviousFieldsValidation}
      />
    </>
  );
};

export default BasicLife;
